import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { analytics } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { CaptchaField } from "@/components/captcha-field";
import { supabase } from "@/integrations/supabase/client";

import { useAuth } from "@/hooks/useAuth";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/login")({
  head: () =>
    privatePageMetadata(
      "/login",
      "Sign in | BeHot.Now",
      "Sign in to BeHot.Now to manage your listings, boosts and wallets.",
    ),
  component: LoginPage,
});


function LoginPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: signupsOpen = true } = useQuery({
    queryKey: ["feature-signups"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "features")
        .maybeSingle();
      const value = (data?.value ?? {}) as { signups?: boolean };
      return value.signups !== false;
    },
  });
  const [mode, setMode] = useState<"signin" | "register" | "reset">("signin");
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [consentFor, setConsentFor] = useState<"google" | "email" | null>(null);


  useEffect(() => {
    if (!user) return;
    let pending: Record<string, unknown> | null = null;
    try {
      const raw = window.localStorage.getItem("behot:pending-consent");
      if (raw) pending = JSON.parse(raw) as Record<string, unknown>;
      window.localStorage.removeItem("behot:pending-consent");
    } catch {
      pending = null;
    }
    const finish = async () => {
      if (pending) await supabase.auth.updateUser({ data: pending });
      void navigate({ to: "/app" });
    };
    void finish();
  }, [user, navigate]);


  useEffect(() => {
    if (!signupsOpen && mode === "register") setMode("signin");
  }, [signupsOpen, mode]);

  // Surface OAuth errors (e.g. sign-up blocked while registrations are closed).
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const search = new URLSearchParams(window.location.search);
    const desc = hash.get("error_description") ?? search.get("error_description");
    if (!desc) return;
    toast.error(
      /closed|Database error/i.test(desc)
        ? t("shell.login.registrationsClosedLong")
        : desc,
    );
    window.history.replaceState(null, "", window.location.pathname);
  }, [t]);

  function switchMode(next: "signin" | "register" | "reset") {
    setMode(next);
    setSent(false);
    setCaptchaInput("");
    setCaptchaKey((k) => k + 1);
  }

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (mode === "register" && !signupsOpen) {
      toast.error(t("shell.login.registrationsClosed"));
      return;
    }
    if (!captchaValid) {
      toast.error(t("shell.login.captchaError"));
      setCaptchaInput("");
      setCaptchaKey((k) => k + 1);
      return;
    }
    if (mode === "register" && !acceptTerms) {
      setConsentFor("email");
      return;
    }
    await runSubmit();
  }

  async function runSubmit() {
    setBusy(true);
    try {
      if (mode === "reset") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setSent(true);
        analytics.action("password_reset_requested", { method: "email" });
        toast.success(t("shell.login.resetLinkSent"));
        return;
      }
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: mode === "register",
          emailRedirectTo: `${window.location.origin}/app`,
          ...(mode === "register"
            ? {
                data: {
                  terms_accepted: true,
                  terms_accepted_at: new Date().toISOString(),
                  marketing_opt_in: acceptMarketing,
                },
              }
            : {}),
        },
      });
      if (error) throw error;
      setSent(true);
      if (mode === "register") analytics.signUpStarted("magic_link");
      else analytics.action("login_link_requested", { method: "magic_link" });
      toast.success(t("shell.login.magicLinkSent"));
    } catch (err) {
      const message = err instanceof Error ? err.message : t("shell.login.sendLinkError");
      toast.error(
        mode === "signin" && /signup|not found|disabled/i.test(message)
          ? t("shell.login.noAccountError")
          : message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    if (mode === "register") {
      if (!signupsOpen) {
        toast.error(t("shell.login.registrationsClosed"));
        return;
      }
      if (!acceptTerms) {
        setConsentFor("google");
        return;
      }
    }
    await runGoogle();
  }

  async function runGoogle() {
    if (mode === "register") {
      try {
        window.localStorage.setItem(
          "behot:pending-consent",
          JSON.stringify({
            terms_accepted: true,
            terms_accepted_at: new Date().toISOString(),
            marketing_opt_in: acceptMarketing,
          }),
        );
      } catch {
        /* storage unavailable — consent is still recorded server-side on first sign-in */
      }
    }
    analytics.action("login_started", { method: "google" });
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/login` },
    });
    if (error) toast.error(t("shell.login.googleError"));
  }

  function confirmConsent() {
    const target = consentFor;
    setConsentFor(null);
    if (target === "google") void runGoogle();
    else if (target === "email") void runSubmit();
  }



  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="text-center">
        <Flame className="mx-auto size-8 text-primary" />
        <h1 className="mt-3 font-display text-2xl font-bold">
          {sent
            ? t("shell.login.checkInbox")
            : mode === "signin"
              ? t("shell.login.signIn")
              : mode === "reset"
                ? t("shell.login.resetTitle")
                : t("shell.login.createAccount")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {sent
            ? mode === "reset"
              ? t("shell.login.resetSentDesc", { email })
              : t("shell.login.magicSentDesc", { email })
            : mode === "signin"
              ? t("shell.login.signInDesc")
              : mode === "reset"
                ? t("shell.login.resetDesc")
                : t("shell.login.registerDesc")}
        </p>
      </div>

      <div className="card-surface mt-8 space-y-4 p-6">
        {sent ? (
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>
              {mode === "reset"
                ? t("shell.login.resetClickHint")
                : t("shell.login.magicClickHint")}
            </p>
            <Button variant="outline" className="w-full" disabled={busy} onClick={() => void submit()}>
              {busy ? t("shell.login.sending") : mode === "reset" ? t("shell.login.resendReset") : t("shell.login.resendMagic")}
            </Button>
            <button type="button" className="hover:text-foreground" onClick={() => setSent(false)}>
              {t("shell.login.useAnotherEmail")}
            </button>
          </div>
        ) : (
          <>
            {mode === "reset" ? (
              <p className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t("shell.login.resetInstructions")}
              </p>
            ) : signupsOpen ? (
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1 text-sm font-medium">
                {(["signin", "register"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => switchMode(m)}
                    className={`rounded-lg px-3 py-2 transition ${
                      mode === m ? "bg-background shadow-sm" : "text-muted-foreground"
                    }`}
                  >
                    {m === "signin" ? t("shell.login.signIn") : t("shell.login.createAccountShort")}
                  </button>
                ))}
              </div>
            ) : (
              <p className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t("shell.login.registrationsClosedNotice")}
              </p>
            )}

            {mode !== "reset" && (
              <>
                <Button variant="outline" className="w-full" onClick={handleGoogle}>
                  {t("shell.login.continueGoogle")}
                </Button>



                <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
                  <span className="h-px flex-1 bg-border" /> {t("shell.login.or")}{" "}
                  <span className="h-px flex-1 bg-border" />
                </div>
              </>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("shell.login.emailLabel")}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <CaptchaField
                key={captchaKey}
                value={captchaInput}
                onChange={setCaptchaInput}
                onValidChange={setCaptchaValid}
              />




              <Button
                type="submit"
                variant="hot"
                className="w-full"
                disabled={busy || !email || !captchaValid}
              >

                {busy
                  ? t("shell.login.sending")
                  : mode === "signin"
                    ? t("shell.login.emailMagicLink")
                    : mode === "reset"
                      ? t("shell.login.emailResetLink")
                      : t("shell.login.createAccountShort")}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                {mode === "reset" ? (
                  <button
                    type="button"
                    className="hover:text-foreground hover:underline"
                    onClick={() => switchMode("signin")}
                  >
                    {t("shell.login.backToSignIn")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="hover:text-foreground hover:underline"
                    onClick={() => switchMode("reset")}
                  >
                    {t("shell.login.forgotPassword")}
                  </button>
                )}
              </p>
            </form>
          </>
        )}
      </div>

      <Dialog open={consentFor !== null} onOpenChange={(open) => !open && setConsentFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("shell.login.consentTitle")}</DialogTitle>
            <DialogDescription>{t("shell.login.consentDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="rounded-xl border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
              {t("shell.login.agreementText")}
            </p>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={acceptTerms}
                onCheckedChange={(v) => setAcceptTerms(v === true)}
                className="mt-0.5"
              />
              <span>
                {t("shell.login.acceptPrefix")}{" "}
                <Link to="/terms" target="_blank" className="text-primary hover:underline">{t("shell.login.terms")}</Link>,{" "}
                <Link to="/privacy" target="_blank" className="text-primary hover:underline">{t("shell.login.privacy")}</Link>,{" "}
                <Link to="/cookie" target="_blank" className="text-primary hover:underline">{t("shell.login.cookiePolicy")}</Link> {t("shell.login.and")}{" "}
                <Link to="/withdrawal" target="_blank" className="text-primary hover:underline">{t("shell.login.withdrawal")}</Link>.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={acceptMarketing}
                onCheckedChange={(v) => setAcceptMarketing(v === true)}
                className="mt-0.5"
              />
              <span>{t("shell.login.marketingOptIn")}</span>
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConsentFor(null)}>
              {t("shell.login.consentCancel")}
            </Button>
            <Button variant="hot" disabled={!acceptTerms} onClick={confirmConsent}>
              {consentFor === "google"
                ? t("shell.login.consentConfirmGoogle")
                : t("shell.login.consentConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
}
